/**
 * Stair, hall and kitchen runner PDP (product.stair-runner.json,
 * product.hall-runner.json, product.kitchen-runner.json).
 * Variant selection stays with variant-selector-component. This file adds the
 * width/length chooser, custom length (snapped to the next stock length),
 * tape and underlay add-ons, and the mobile sticky bar.
 *
 * Works in every market: sizes are read from the variant labels whatever the
 * language or units ("60cm", "Breite auswählen: 60 cm", "2ft 4in"), and every
 * label, guide and message follows the market's units and copy (RR below).
 */

import { ThemeBus } from "@theme/events-bus";
import { EVENTS } from "@theme/event-constants";
import { shopifyCartUrl, updateCartBubble } from "@theme/utilities";
import {
  createBundleId,
  customLengthVariantFor,
  exactLengthQuote,
  exactLineProperties,
  exactTapeProperties,
  exactTapeVariant,
  exactUnderlayProperties,
  exactUnderlayVariant,
  formatFeetInches,
  isCustomLengthVariant,
  maxFixedLengthCm,
} from "@theme/stair-exact-length";

/* ------------------------------------------------------------------ */
/* Market helpers: units, number/money format and copy.                */
/* Settings come from snippets/rr-pdp-config.liquid (#rr-pdp-config).  */
/* ------------------------------------------------------------------ */

const RR = (() => {
  let cfg = {};
  try {
    cfg = JSON.parse(document.getElementById("rr-pdp-config")?.textContent || "{}");
  } catch {
    cfg = {};
  }
  const strings = cfg.strings || {};
  const imperial = cfg.units === "imperial";
  const dec = cfg.decimal || ".";
  const sp = cfg.unitSpace || "";

  /** Copy from rr_pdp locales, with {placeholders} filled in. */
  const t = (key, vars = {}, fallback = "") => {
    const raw = strings[key] ?? fallback ?? key;
    return String(raw).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
  };

  const num = (n, dp = 1) => {
    const s = String(Math.round(Number(n) * 10 ** dp) / 10 ** dp);
    return dec === "." ? s : s.replace(".", dec);
  };

  /** Strip "Select Width:" style prefixes and tidy spacing. */
  const clean = (label) =>
    String(label || "")
      .replace(/^.*:\s*/, "")
      .replace(/(\d)\s*inch(es)?\b/gi, "$1in")
      .replace(/\s+/g, " ")
      .trim();

  const isImperialLabel = (label) => /\d\s*(ft|feet|foot|in\b|inch|'|"|’|″)/i.test(String(label || ""));

  /** Any size label to centimetres: 60cm, 70 cm, 10.2 m, 10,2 m, 8mm, 2ft 4in, 5' 3", 1ft. */
  const toCm = (label) => {
    const s = clean(label).toLowerCase().replace(/,/g, ".").replace(/[’′]/g, "'").replace(/[”″]/g, '"');
    if (!s) return 0;
    if (isImperialLabel(s)) {
      const ft = s.match(/(\d+(?:\.\d+)?)\s*(?:ft|feet|foot|')/);
      const inch = s.match(/(\d+(?:\.\d+)?)\s*(?:in(?:ch(?:es)?)?\b|"|$)/);
      const ftVal = ft ? Number(ft[1]) : 0;
      let inVal = inch ? Number(inch[1]) : 0;
      if (ft && inch && inch.index === ft.index) inVal = 0;
      return Math.round((ftVal * 12 + inVal) * 2.54);
    }
    const compact = s.replace(/\s/g, "");
    const n = parseFloat(compact);
    if (!Number.isFinite(n)) return 0;
    if (/mm$/.test(compact)) return Math.round(n / 10);
    if (/cm$/.test(compact)) return Math.round(n);
    if (/m$/.test(compact)) return Math.round(n * 100);
    return Math.round(n);
  };

  const ftIn = (cm) => {
    const inches = Math.round(cm / 2.54);
    const f = Math.floor(inches / 12);
    const i = inches % 12;
    if (!f) return `${i}in`;
    return i ? `${f}ft ${i}in` : `${f}ft`;
  };
  const metric = (cm) => (cm >= 1000 ? `${num(cm / 100)}${sp}m` : `${Math.round(cm)}${sp}cm`);
  /** A length in the market's units (runner lengths, rug sides). */
  const len = (cm) => (imperial ? ftIn(cm) : metric(cm));
  /** A small gap or border, e.g. 45cm / 18in. */
  const gap = (cm) => (imperial ? `${Math.round(cm / 2.54)}in` : `${Math.round(cm)}${sp}cm`);
  /** A two-dimension size, e.g. 160 × 230cm / 5ft 3in × 7ft 7in. */
  const size = (w, l) => (imperial ? `${ftIn(w)} × ${ftIn(l)}` : `${Math.round(w)} × ${Math.round(l)}${sp}cm`);
  /** Room size in metres or feet. */
  const room = (w, l) => (imperial ? `${ftIn(w)} × ${ftIn(l)}` : `${num(w / 100)} × ${num(l / 100)}${sp}m`);
  /** Show a variant's own size label in a tidy, market-correct way. */
  const label = (raw) => {
    const c = clean(raw);
    if (!c) return "";
    if (isImperialLabel(c) || /\dmm$/i.test(c.replace(/\s/g, ""))) return c;
    const cm = toCm(c);
    return cm ? metric(cm) : c;
  };

  /** Format pence/cents the same way Liquid's money filter does for this market. */
  const sample = String(cfg.moneySample || "");
  const sampleMatch = sample.match(/1(\D?)234(\D)56/);
  const money = (cents) => {
    const value = (Number(cents) || 0) / 100;
    if (sampleMatch) {
      const [all, thousands, decimal] = sampleMatch;
      const [i, f] = value.toFixed(2).split(".");
      const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return sample.replace(all, `${int}${decimal}${f}`);
    }
    return new Intl.NumberFormat(cfg.dateLocale || window.shopLocale || "en-GB", {
      style: "currency",
      currency: window.shopCurrency || "GBP",
    }).format(value);
  };

  const date = (d) =>
    d.toLocaleDateString(cfg.dateLocale || "en-GB", { weekday: "short", day: "numeric", month: "short" });

  return { cfg, t, imperial, clean, isImperialLabel, toCm, ftIn, metric, len, gap, size, room, label, money, num, date };
})();

const LENGTH_RE = /length|longueur|l(ä|a)nge|lunghezza|longitud/i;
/* Per-centimetre "Custom Length" variants are kept out of the stock-length chooser and
   used by the exact-length box instead (isCustomLengthVariant comes from @theme/stair-exact-length). */
const WIDTH_RE = /width|largeur|breite|larghezza|anchura|ancho/i;
const near = (a, b, tol = 2) => Math.abs((Number(a) || 0) - (Number(b) || 0)) <= tol;

/* Custom length limits per unit system. Stock lengths step every 30cm (sold as 1ft in the USA and Canada).
   In the USA and Canada the customer types feet and inches; the value is kept as total inches. */
const CUSTOM = RR.imperial
  ? { min: 12, max: 600, toCm: (n) => n * 2.54, show: (n) => formatFeetInches(n), step: "1ft", maxCm: 1530, maxText: "50ft" }
  : { min: 30, max: 1499, toCm: (n) => n, show: (n) => `${n}${RR.cfg.unitSpace || ""}cm`, step: `30${RR.cfg.unitSpace || ""}cm`, maxCm: 1500, maxText: `1499${RR.cfg.unitSpace || ""}cm` };

/* ------------------------------------------------------------------ */
/* Analytics: Noibu custom events, sent through rr-analytics.js        */
/* (window.rrTrack / window.rrAttr). rr-analytics.js is deferred, so   */
/* anything sent before it has run waits on a small queue it replays.  */
/* Tracking must never break the buy box, so every call is guarded.    */
/* ------------------------------------------------------------------ */

function track(name, props = {}) {
  try {
    if (typeof window.rrTrack === "function") {
      window.rrTrack(name, props);
      return;
    }
    const q = (window.__rrTrackQ = window.__rrTrackQ || []);
    if (q.length < 100) q.push([name, props]);
  } catch {
    /* no-op */
  }
}

function trackAttr(name, value) {
  try {
    if (typeof window.rrAttr === "function") {
      window.rrAttr(name, value);
      return;
    }
    const q = (window.__rrAttrQ = window.__rrAttrQ || []);
    if (q.length < 20) q.push([name, value]);
  } catch {
    /* no-op */
  }
}

const productHandle = () =>
  window.__rrContext?.item_handle || (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || "";

const majorUnits = (cents) => (Number.isFinite(Number(cents)) ? Math.round(Number(cents)) / 100 : undefined);

class StairRunner extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const raw = this.querySelector("[data-stair-data]")?.textContent || "{}";
    try {
      this.data = JSON.parse(raw);
    } catch {
      this.data = { variants: [], tapes: [], underlay: null, minPrice: 0 };
    }
    const names = this.data.options || [];
    let lengthIndex = names.findIndex((n) => LENGTH_RE.test(n));
    let widthIndex = names.findIndex((n) => WIDTH_RE.test(n));
    // Fall back on position (length first, width second) when option names are translated into something unexpected.
    if (lengthIndex < 0 && widthIndex < 0 && names.length >= 2) {
      lengthIndex = 0;
      widthIndex = 1;
    } else if (lengthIndex < 0) lengthIndex = widthIndex === 0 ? 1 : 0;
    else if (widthIndex < 0) widthIndex = lengthIndex === 0 ? 1 : 0;
    const parsed = (this.data.variants || []).map((v) => {
      const options = v.options || [];
      return {
        ...v,
        lengthLabel: options[lengthIndex] || "",
        widthLabel: options[widthIndex] || "",
        lengthCm: RR.toCm(options[lengthIndex]),
        widthCm: RR.toCm(options[widthIndex]),
        lengthText: RR.label(options[lengthIndex]),
        widthText: RR.label(options[widthIndex]),
      };
    });
    this.variants = parsed.filter((v) => !isCustomLengthVariant(v));
    /* Per-cm "Custom Length" variants: one per width, priced in the cart from _Length cm. */
    this.customVariants = parsed.filter((v) => isCustomLengthVariant(v));

    this.widthCm = 0;
    this.length = "";
    this.customOn = false;
    this.customValue = "";
    this.tapeOn = false;
    this.tapeName = this.data.tapes?.[0]?.name || "";
    this.underlayOn = false;
    this.underlayThickness = "";
    /* Anti-slip qualities (hall and kitchen runners). data.underlay always points at the
       quality in use, so the cart and total code below only ever sees one underlay. */
    this.antiSlip = this.data.antiSlip || { copy: {}, qualities: [] };
    /* Copy comes from the block settings; these are the fallbacks for an older block JSON. */
    const asCopy = this.antiSlip.copy || {};
    const asDefaults = {
      choose: "Choose your anti-slip",
      cutTo: "{name}, cut to fit: {size}",
      swapped: "{width} {name} is out of stock, so we have picked {width} {pick}.",
      none: "No anti-slip is in stock in a width that fits this runner right now. Call us and we will help.",
      oos: "{width} out of stock",
      noWidth: "Not made narrow enough",
    };
    Object.keys(asDefaults).forEach((k) => {
      if (!asCopy[k]) asCopy[k] = asDefaults[k];
    });
    this.antiSlip.copy = asCopy;
    this.qualities = this.#isFlat() ? (this.antiSlip.qualities || []).filter((q) => q?.feed) : [];
    if (this.qualities.length) this.data.underlay = this.qualities[0];
    this.qualityPicked = "";
    this.steps = 0;
    this.pending = null;
    this.applying = false;

    this.form = this.closest("form");
    this.source = this.querySelector(".stair-source");
    this.#bindSource();
    this.#buildMenus();
    this.#preselectSmallest();
    this.#bind();
    this.#setupTape();
    this.#setupUnderlay();
    this.#watchCartButton();
    this.#relabelSample();
    this.#render();
    this.#mountSticky();
    this.#watchBuyBoxSeen();
    this.feedsReady = this.#loadFeeds();
  }

  /* pdp_buybox_seen: once per page, when half the buy box is on screen. On a phone the buy
     box can be taller than two screens, so half the viewport filled by it also counts. */
  #watchBuyBoxSeen() {
    try {
      if (!this.matches(".stair-buy") || !("IntersectionObserver" in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          if (!entry?.isIntersecting) return;
          const half = entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= window.innerHeight * 0.5;
          if (!half) return;
          io.disconnect();
          track("pdp_buybox_seen", { ms_since_load: Math.round(performance.now()) });
        },
        { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] },
      );
      io.observe(this);
    } catch {
      /* no-op */
    }
  }

  /* pdp_atc_click properties, built before the add runs. Never let a tracking
     error stop the add to cart: fall back to the source alone. */
  #atcPropsSafe(source) {
    try {
      return this.#atcProps(source);
    } catch {
      return { source };
    }
  }

  /* Common properties for the add-to-cart event. */
  #atcProps(source) {
    const state = this.#state();
    const variant = this.#currentVariant();
    let extra = 0;
    try {
      extra = variant ? this.#extras().extra : 0;
    } catch {
      extra = 0;
    }
    return {
      source,
      state,
      variant_id: variant?.id ? String(variant.id) : undefined,
      width: this.widthText || undefined,
      length_cm: this.#runnerLengthCm() || undefined,
      is_custom: this.customOn,
      tape: this.tapeOn ? this.tapeName || "yes" : "none",
      underlay: this.underlayOn ? this.underlayThickness || (this.qualities.length ? this.data.underlay?.handle : "") || "yes" : "none",
      price: variant ? majorUnits(variant.price + extra) : undefined,
      quantity: this.#quantity(),
      currency: window.shopCurrency || window.Shopify?.currency?.active || undefined,
    };
  }

  /* pdp_custom_length_entered: once per settled value (the US feet and inches boxes
     both fire change, so a repeat of the same total is skipped). */
  #trackCustomLength() {
    const n = parseInt(this.customValue, 10);
    if (!n || String(n) === this._trackedCustom) return;
    this._trackedCustom = String(n);
    const quote = this.#exact();
    const variant = this.#currentVariant();
    const cm = quote?.ok ? quote.cm : Math.round(CUSTOM.toCm(n));
    track("pdp_custom_length_entered", {
      cm,
      display: quote?.inputLabel || quote?.display || CUSTOM.show(n),
      price: variant ? majorUnits(variant.price) : undefined,
      valid: Boolean(quote ? quote.ok : this.#roundedCustom()),
      unit: RR.imperial ? "ft" : "cm",
      product_handle: productHandle(),
    });
    trackAttr("custom_length_used", "true");
  }

  /* Tape and underlay variants come from each product's own feed (/products/<handle>.js),
     not the page: pricing hundreds of add-on variants in Liquid made the page too slow. */
  async #loadFeeds() {
    const load = async (target) => {
      if (!target?.feed || target.variants?.length) return;
      try {
        const res = await fetch(target.feed, { headers: { Accept: "application/json" } });
        if (!res.ok) return;
        const product = await res.json();
        target.variants = (product.variants || []).map((v) => ({
          id: v.id,
          options: v.options || [v.option1, v.option2, v.option3].filter((o) => o != null),
          price: v.price,
          available: v.available,
        }));
      } catch {
        /* Leave the add-on without variants: it shows as unavailable rather than breaking the page. */
      }
    };
    this.feedsLoading = true;
    const targets = [...new Set([...(this.data.tapes || []), this.data.underlay, ...this.qualities].filter(Boolean))];
    await Promise.all(targets.map(load));
    this.feedsLoading = false;
    if (this.qualities.length) {
      /* A quality whose feed failed (or is not sold in this market) is dropped. */
      this.qualities = this.qualities.filter((q) => q.variants?.length);
      if (this.qualities.length) this.data.underlay = this.qualities[0];
    }
    this._underlayOptions = null;
    const mmRow = this.querySelector("[data-stair-underlay-mm]");
    if (mmRow) delete mmRow.dataset.ready;
    /* A feed that failed to load hides its card, as if the add-on were not offered. */
    if (this.data.underlay && !this.data.underlay.variants?.length) {
      this.underlayOn = false;
      const card = this.querySelector("[data-stair-underlay-card]");
      if (card) card.hidden = true;
    }
    if ((this.data.tapes || []).length && !this.data.tapes.some((t) => t.variants?.length)) {
      this.tapeOn = false;
      const card = this.querySelector("[data-stair-tape-card]");
      if (card) card.hidden = true;
    }
    this.#render();
  }

  get widthText() {
    return this.widths?.find((w) => near(w.cm, this.widthCm, 1))?.text || "";
  }

  #lengthText(label) {
    return this.lengthChoices?.find((c) => c.label === label)?.text || RR.label(label);
  }

  #bindSource() {
    const selects = [...(this.source?.querySelectorAll("select[data-variant-option]") || [])];
    this.widthSelect = selects.find((sel) => WIDTH_RE.test(this.#selectLabel(sel)));
    this.lengthSelect = selects.find((sel) => LENGTH_RE.test(this.#selectLabel(sel)));
    if (!this.widthSelect || !this.lengthSelect) {
      // Option names translated into something we don't recognise: match the selects by their values instead.
      const widthValues = new Set(this.variants.map((v) => v.widthLabel));
      const lengthValues = new Set(this.variants.map((v) => v.lengthLabel));
      const has = (sel, set) => [...sel.options].some((o) => set.has(o.value));
      this.widthSelect = this.widthSelect || selects.find((sel) => has(sel, widthValues));
      this.lengthSelect = this.lengthSelect || selects.find((sel) => sel !== this.widthSelect && has(sel, lengthValues));
    }
  }

  #selectLabel(sel) {
    return sel.parentElement?.parentElement?.querySelector("label")?.textContent || "";
  }

  #buildMenus() {
    const widths = [];
    const lengths = [];
    this.variants.forEach((v) => {
      if (v.widthCm && !widths.some((w) => near(w.cm, v.widthCm, 1))) widths.push({ cm: v.widthCm, text: v.widthText });
      if (v.lengthLabel && !lengths.some((l) => l.label === v.lengthLabel)) {
        lengths.push({ label: v.lengthLabel, cm: v.lengthCm, text: v.lengthText });
      }
    });
    widths.sort((a, b) => a.cm - b.cm);
    lengths.sort((a, b) => a.cm - b.cm);
    this.widths = widths;
    this.widthMenu = this.querySelector('[data-stair-select="width"]');
    this.lengthMenu = this.querySelector('[data-stair-select="length"]');
    this.widthMenu.innerHTML = widths
      .map((w) => `<option value="${w.cm}">${escapeHtml(w.text)}</option>`)
      .join("");
    this.lengthChoices = lengths;
  }

  #preselectSmallest() {
    const widths = this.widths.filter((w) => this.variants.some((v) => v.available && near(v.widthCm, w.cm, 1)));
    const width = widths[0];
    if (!width) return;
    const length = this.variants
      .filter((v) => v.available && near(v.widthCm, width.cm, 1))
      .sort((a, b) => a.lengthCm - b.lengthCm)[0];
    if (!length) return;
    this.widthCm = width.cm;
    this.length = length.lengthLabel;
    this.#syncVariant();
  }

  #bind() {
    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-stair-custom-toggle]")) {
        this.customOn = !this.customOn;
        if (this.customOn) track("pdp_custom_length_open", { product_handle: productHandle() });
        this.#render();
        if (this.customOn) this.querySelector("[data-stair-custom-input], [data-stair-custom-ft]")?.focus();
        return;
      }
      if (e.target.closest("[data-stair-guide-toggle]")) {
        const guide = this.querySelector("[data-stair-guide]");
        guide.hidden = !guide.hidden;
        if (!guide.hidden) track("pdp_guide_open", { product_handle: productHandle() });
        return;
      }
      if (e.target.closest("[data-stair-guide-apply]")) {
        this.#applyGuide();
        return;
      }
      const tape = e.target.closest("[data-stair-tape]");
      if (tape) {
        this.tapeName = tape.dataset.stairTape;
        this.#render();
        track("pdp_addon_toggle", { addon: "tape", control: "colour", checked: this.tapeOn, tape_colour: this.tapeName });
        return;
      }
      if (e.target.closest("[data-stair-steps-dec]")) {
        this.steps = Math.max(0, (this.steps || 0) - 1);
        this.#render();
        track("pdp_steps_change", { steps: this.steps, control: "dec" });
        return;
      }
      if (e.target.closest("[data-stair-steps-inc]")) {
        this.steps = Math.min(40, (this.steps || 0) + 1);
        this.#render();
        track("pdp_steps_change", { steps: this.steps, control: "inc" });
        return;
      }
      const quality = e.target.closest("[data-stair-antislip-opt]");
      if (quality) {
        if (quality.getAttribute("aria-disabled") === "true") return;
        this.qualityPicked = quality.dataset.stairAntislipOpt;
        this.#render();
        track("pdp_addon_toggle", { addon: "underlay", control: "quality", checked: this.underlayOn, option: this.qualityPicked });
        return;
      }
      const thick = e.target.closest("[data-stair-underlay-mm-opt]");
      if (thick) {
        this.underlayThickness = thick.dataset.stairUnderlayMmOpt;
        this.#render();
        track("pdp_addon_toggle", { addon: "underlay", control: "thickness", checked: this.underlayOn, option_mm: this.underlayThickness });
      }
    });

    this.querySelector("[data-stair-custom-input]")?.addEventListener("input", (e) => {
      let digits = e.target.value.replace(/\D/g, "").slice(0, 4);
      const cap = this.#exactVariant() ? this.#exactMaxInput() : CUSTOM.max;
      if (digits && cap && Number(digits) > cap) digits = String(cap);
      e.target.value = digits;
      this.customValue = digits;
      // Clear the "choose a length" highlight from a blocked add once a length is typed.
      if (digits) this.querySelector('[data-stair-field="length"]')?.classList.remove("is-invalid");
      this.#render();
    });

    this.querySelector("[data-stair-custom-input]")?.addEventListener("change", () => this.#trackCustomLength());

    /* USA and Canada: feet and inches boxes. customValue holds the total in inches, so pricing,
       limits and the cart line work exactly as they do for a single box. */
    const ftInput = this.querySelector("[data-stair-custom-ft]");
    const inInput = this.querySelector("[data-stair-custom-in]");
    const onFeetInches = () => {
      const ft = ftInput.value.replace(/\D/g, "").slice(0, 2);
      const inch = inInput.value.replace(/\D/g, "").slice(0, 3);
      if (ftInput.value !== ft) ftInput.value = ft;
      if (inInput.value !== inch) inInput.value = inch;
      this.customValue = ft || inch ? String(Number(ft || 0) * 12 + Number(inch || 0)) : "";
      if (this.customValue) this.querySelector('[data-stair-field="length"]')?.classList.remove("is-invalid");
      this.#render();
    };
    ftInput?.addEventListener("input", onFeetInches);
    inInput?.addEventListener("input", onFeetInches);
    ftInput?.addEventListener("change", () => this.#trackCustomLength());
    inInput?.addEventListener("change", () => this.#trackCustomLength());
    /* 12in or more typed in the inches box (say a total of 131in) moves into feet when the box is left: 10ft 11in. */
    inInput?.addEventListener("blur", () => {
      const total = Number(this.customValue) || 0;
      if (!ftInput || Number(inInput.value) < 12 || !total) return;
      ftInput.value = String(Math.floor(total / 12));
      inInput.value = String(total % 12);
    });

    this.querySelectorAll("[data-stair-g]").forEach((input) => {
      input.addEventListener("input", () => {
        input.value = input.value.replace(/\D/g, "").slice(0, 3);
        this.#renderGuide();
      });
    });

    this.querySelector("[data-stair-tape-check]")?.addEventListener("change", (e) => {
      this.tapeOn = e.target.checked;
      this.#render();
      if (!this.tapeOn) this.#showGalleryPhoto(0);
      track("pdp_addon_toggle", { addon: "tape", control: "checkbox", checked: this.tapeOn, tape_colour: this.tapeName });
    });
    this.querySelector("[data-stair-underlay-check]")?.addEventListener("change", (e) => {
      this.underlayOn = e.target.checked;
      this.#render();
      track("pdp_addon_toggle", { addon: "underlay", control: "checkbox", checked: this.underlayOn, option_mm: this.underlayThickness });
    });
    this.querySelector("[data-stair-steps-input]")?.addEventListener("input", (e) => {
      const n = Math.min(40, Math.max(0, parseInt(e.target.value.replace(/\D/g, ""), 10) || 0));
      e.target.value = n ? String(n) : "";
      this.steps = n;
      this.#render();
    });
    /* Typed step counts are tracked once the box is left, not on every keystroke. */
    this.querySelector("[data-stair-steps-input]")?.addEventListener("change", () => {
      track("pdp_steps_change", { steps: this.steps, control: "input" });
    });

    this.querySelectorAll("[data-stair-select]").forEach((select) => {
      select.addEventListener("change", () => {
        this.#choose(select.dataset.stairSelect, select.value);
        const dimension = select.dataset.stairSelect === "width" ? "width" : "length";
        track("pdp_size_change", {
          dimension,
          value: select.selectedOptions?.[0]?.textContent?.trim() || select.value,
          value_cm: dimension === "width" ? Number(select.value) || undefined : RR.toCm(select.value) || undefined,
          unit: RR.imperial ? "ft" : "cm",
          product_handle: productHandle(),
        });
      });
    });

    /* Listen on the document in the capture phase, as rug-pdp.js does, so this runs before
       product-form.js reads the form (it builds FormData in its own capture listener on the
       form). Listening on the form itself made the order depend on which script loaded first,
       and the exact-length inputs were read while still empty. */
    document.addEventListener("submit", (e) => this.#onSubmit(e), true);
    this.querySelector('input[name="quantity"]')?.addEventListener("input", () => this.#render());
    this.querySelector('input[name="quantity"]')?.addEventListener("change", () => this.#render());
    ThemeBus.addEventListener(EVENTS.cartItemAdded, () => this.#addPending());
  }

  #fillLengths() {
    if (!this.lengthMenu) return;
    /* The options only change with the width, so rebuild them only then (this runs on every keystroke). */
    const key = String(this.widthCm);
    if (this._lengthsFor === key) {
      this.lengthMenu.value = this.length || "";
      return;
    }
    this._lengthsFor = key;
    const items = this.lengthChoices.map((choice) => {
      const variant = this.#variantFor(this.widthCm, choice.label);
      const unavailable = this.widthCm && variant && !variant.available;
      return `<option value="${escapeAttr(choice.label)}"${unavailable ? " disabled" : ""}>${escapeHtml(choice.text)}${unavailable ? ` (${escapeHtml(RR.t("stock_out"))})` : ""}</option>`;
    });
    this.lengthMenu.innerHTML = items.join("");
    this.lengthMenu.value = this.length || "";
  }

  #choose(which, value) {
    if (which === "width") this.widthCm = Number(value) || 0;
    if (which === "length") {
      const variant = this.#variantFor(this.widthCm, value);
      if (value && this.widthCm && variant && !variant.available) return;
      this.length = value;
      if (value) {
        this.customOn = false;
        this.customValue = "";
      }
    }
    this.querySelector('[data-stair-field="width"]')?.classList.remove("is-invalid");
    this.querySelector('[data-stair-field="length"]')?.classList.remove("is-invalid");
    this.#syncVariant();
    this.#render();
  }

  #variantFor(widthCm, length) {
    if (!widthCm || !length) return null;
    const lengthCm = RR.toCm(length);
    const matches = this.variants.filter(
      (v) => near(v.widthCm, widthCm, 1) && (v.lengthLabel === length || near(v.lengthCm, lengthCm, 1)),
    );
    if (!matches.length) return null;
    const available = matches.filter((v) => v.available);
    const pool = available.length ? available : matches;
    return pool.find((v) => !/select/i.test(v.widthLabel)) || pool[0];
  }

  /* ---- Exact length: the per-cm Custom Length variant for the chosen width ---- */

  /** True when any width can be cut to the centimetre, so the toggle is worth showing. */
  #hasExactLength() {
    return (this.customVariants || []).some((v) => v.available);
  }

  /** The Custom Length variant for the chosen width, or null (then custom mode snaps to stock lengths). */
  #exactVariant() {
    if (!this.widthCm) return null;
    const variant = customLengthVariantFor(this.customVariants, this.widthCm);
    return variant && variant.available ? variant : null;
  }

  /** Longest stock length for the width, in cm. Falls back to the theme-wide cap. */
  #exactMaxCm() {
    return maxFixedLengthCm(this.variants, this.widthCm) || CUSTOM.maxCm;
  }

  /** Largest number the customer can type for the width (cm, or whole inches). */
  #exactMaxInput() {
    const maxCm = this.#exactMaxCm();
    return RR.imperial ? Math.floor(maxCm / 2.54) : maxCm;
  }

  /** Price the typed length against the Custom Length variant. Null outside exact mode. */
  #exact() {
    if (!this.customOn) return null;
    const variant = this.#exactVariant();
    if (!variant) return null;
    return exactLengthQuote({
      raw: this.customValue,
      imperial: RR.imperial,
      perCmCents: variant.price,
      comparePerCmCents: variant.compare,
      minCm: CUSTOM.min,
      minInches: 12,
      maxCm: this.#exactMaxCm(),
      unitSpace: RR.cfg.unitSpace || "",
    });
  }

  /** The Custom Length variant dressed as a priced size, so price, total, stock and sticky bar need no special cases. */
  #exactSelection() {
    const quote = this.#exact();
    const variant = this.#exactVariant();
    if (!quote?.ok || !variant) return null;
    return {
      ...variant,
      price: quote.priceCents,
      compare: quote.compareCents || 0,
      lengthCm: quote.cm,
      lengthText: quote.inputLabel,
      exact: quote,
    };
  }

  #roundedCustom() {
    const n = parseInt(this.customValue, 10);
    if (!n || n < CUSTOM.min) return null;
    const needCm = CUSTOM.toCm(n);
    const choice = this.lengthChoices.find((c) => c.cm >= needCm - 0.5 && c.cm <= CUSTOM.maxCm) || null;
    if (!choice) return null;
    return { input: CUSTOM.show(n), cm: needCm, label: choice.label, text: choice.text, exact: near(choice.cm, needCm, 0.5) };
  }

  #currentVariant() {
    if (this.customOn) {
      if (this.#exactVariant()) return this.#exactSelection();
      const rounded = this.#roundedCustom();
      if (!rounded) return null;
      return this.#variantFor(this.widthCm, rounded.label);
    }
    return this.#variantFor(this.widthCm, this.length);
  }

  #syncVariant() {
    const variant = this.#currentVariant();
    if (!variant || !this.widthSelect || !this.lengthSelect) return;
    const idInput = this.form?.querySelector('input[name="id"]');
    /* Already on this variant (typing 301, 302... in the custom box): skip the change events,
       which make the theme's variant scripts redo their work on every keystroke. */
    if (
      this.widthSelect.value === variant.widthLabel &&
      this.lengthSelect.value === variant.lengthLabel &&
      (!idInput || String(idInput.value) === String(variant.id))
    ) {
      return;
    }
    this.applying = true;
    this.widthSelect.value = variant.widthLabel;
    this.widthSelect.dispatchEvent(new Event("change", { bubbles: true }));
    this.lengthSelect.value = variant.lengthLabel;
    this.lengthSelect.dispatchEvent(new Event("change", { bubbles: true }));
    if (idInput) idInput.value = variant.id;
    this.applying = false;
  }

  #state() {
    if (!this.widthCm) return "width";
    if (this.customOn) {
      if (this.#exactVariant()) {
        if (!this.#exact()?.ok) return "length";
      } else {
        const n = parseInt(this.customValue, 10);
        if (!n || n < CUSTOM.min) return "length";
      }
    } else if (!this.length) return "length";
    const variant = this.#currentVariant();
    if (!variant || !variant.available) return "oos";
    return "add";
  }

  #isFlat() {
    return (
      this.hasAttribute("data-hall") ||
      this.hasAttribute("data-kitchen") ||
      document.body.hasAttribute("data-hall-pdp") ||
      document.body.hasAttribute("data-kitchen-pdp")
    );
  }

  #isKitchen() {
    return this.hasAttribute("data-kitchen") || document.body.hasAttribute("data-kitchen-pdp");
  }

  #runnerLengthCm() {
    if (this.customOn) {
      if (this.#exactVariant()) return this.#exact()?.cm || 0;
      const rounded = this.#roundedCustom();
      return rounded ? RR.toCm(rounded.label) : 0;
    }
    return this.#currentVariant()?.lengthCm || 0;
  }

  #quantity() {
    return Math.max(1, parseInt(this.querySelector('input[name="quantity"]')?.value, 10) || 1);
  }

  #tapeMatch() {
    const variant = this.#currentVariant();
    const tape = (this.data.tapes || []).find((t) => t.name === this.tapeName) || this.data.tapes?.[0];
    if (!tape) return null;
    const shortest = this.lengthChoices?.[0]?.cm || 30;
    if (!variant) {
      const rate = (tape.variants || []).find(
        (v) => v.available && !isCustomLengthVariant(v) && v.options?.some((o) => near(RR.toCm(o), shortest, 1)),
      );
      return { tape, variant: null, rate: rate?.price || 0 };
    }
    if (variant.exact) {
      /* Exact length: the tape's own per-cm variant for this width, priced per cm × cm.
         Without one, the next stock tape length up (never a shorter one). */
      const perCm = exactTapeVariant(tape, variant.widthCm, variant.lengthCm, RR.toCm);
      if (perCm) return { tape, variant: perCm, rate: 0 };
      const stock = (tape.variants || []).filter((v) => v.available && !isCustomLengthVariant(v));
      const longEnough = stock
        .map((v) => {
          const cms = v.options.map((o) => RR.toCm(o));
          const widthAt = cms.findIndex((c) => near(c, variant.widthCm, 1));
          const len = widthAt < 0 ? 0 : Math.max(0, ...cms.filter((_, i) => i !== widthAt));
          return { v, len };
        })
        .filter(({ len }) => len && len >= variant.lengthCm - 2)
        .sort((a, b) => a.len - b.len);
      return { tape, variant: longEnough[0]?.v || null, rate: 0 };
    }
    const match = (tape.variants || []).find(
      (v) =>
        v.available &&
        !isCustomLengthVariant(v) &&
        v.options.some((o) => near(RR.toCm(o), variant.lengthCm, 1)) &&
        v.options.some((o) => near(RR.toCm(o), variant.widthCm, 1)),
    );
    return { tape, variant: match || null, rate: 0 };
  }

  /* Stair underlay packs: one option is the thickness (8mm / 0.3inch - Standard), the other the pad count. */
  #underlayOptions() {
    if (this._underlayOptions) return this._underlayOptions;
    const pack = this.data.underlay;
    const variants = (pack?.variants || []).filter((v) => v.available);
    const optCount = Math.max(0, ...variants.map((v) => (v.options || []).length));
    let thickIdx = -1;
    for (let i = 0; i < optCount; i += 1) {
      if (variants.some((v) => /\d\s*(mm|in\b|inch|")/i.test(v.options?.[i] || ""))) {
        thickIdx = i;
        break;
      }
    }
    const rows = variants
      .map((v) => {
        const opts = v.options || [];
        const thickness = thickIdx >= 0 ? opts[thickIdx] || "" : "";
        const padText = opts.filter((_, i) => i !== thickIdx).join(" ") || v.title || "";
        const pads = Number((padText.match(/(\d+)/) || [])[1] || 0);
        return { ...v, pads, thickness, padText: RR.clean(padText) };
      })
      .filter((v) => v.pads);
    const thicknesses = [];
    rows.forEach((v) => {
      if (v.thickness && !thicknesses.includes(v.thickness)) thicknesses.push(v.thickness);
    });
    thicknesses.sort((a, b) => parseFloat(a.replace(",", ".")) - parseFloat(b.replace(",", ".")));
    this._underlayOptions = { rows, thicknesses };
    return this._underlayOptions;
  }

  #underlayMatch() {
    const pack = this.data.underlay;
    if (!pack) return null;
    if (this.#isFlat()) return this.qualities.length ? this.#antiSlipChoice().match : this.#antiSlipMatch(pack);
    if (!this.steps) return null;
    const { rows, thicknesses } = this.#underlayOptions();
    const chosen = this.underlayThickness || thicknesses[0] || "";
    const preferred = rows.filter((v) => !chosen || v.thickness === chosen);
    const pool = (preferred.length ? preferred : rows).sort((a, b) => a.pads - b.pads);
    return pool.find((v) => v.pads >= this.steps) || pool[pool.length - 1] || null;
  }

  #antiSlipMatch(pack) {
    const lengthCm = this.#runnerLengthCm();
    const current = this.#currentVariant();
    const widthCm = current?.widthCm || this.widthCm;
    if (!lengthCm) return null;
    /* Exact length: the underlay's own per-cm Custom Length variant, cut to the runner.
       Without an available one, the stock size below. */
    if (current?.exact?.ok) {
      const perCm = exactUnderlayVariant(pack, widthCm, current.exact.cm, RR.toCm);
      if (perCm) return { ...perCm, lenLabel: current.exact.inputLabel };
    }
    const lenIdx = Number(pack.lengthIndex);
    const widIdx = Number(pack.widthIndex);
    const TOL = 3;
    const variants = (pack.variants || [])
      .filter((v) => v.available && !isCustomLengthVariant(v))
      .map((v) => {
        const opts = v.options || [];
        const lenLabel = Number.isInteger(lenIdx) && lenIdx >= 0 ? opts[lenIdx] : "";
        const widLabel = Number.isInteger(widIdx) && widIdx >= 0 ? opts[widIdx] : "";
        return { ...v, len: RR.toCm(lenLabel), wid: RR.toCm(widLabel), lenLabel, widLabel };
      })
      .filter((v) => v.len);
    const longEnough = variants.filter((v) => v.len >= lengthCm - TOL);
    if (!longEnough.length) return null;
    const minLen = Math.min(...longEnough.map((v) => v.len));
    const atLen = longEnough.filter((v) => Math.abs(v.len - minLen) <= TOL);
    if (widthCm) {
      const exact = atLen.find((v) => Math.abs(v.wid - widthCm) <= TOL);
      if (exact) return exact;
      const down = atLen.filter((v) => v.wid <= widthCm + TOL).sort((a, b) => b.wid - a.wid)[0];
      if (down) return down;
      return atLen.sort((a, b) => a.wid - b.wid)[0];
    }
    return atLen[0] || null;
  }

  /* One anti-slip quality against the runner. The underlay is never wider than the runner:
     the widest width in stock that is no wider than the runner (60cm for a 60 or 70cm runner,
     80cm for 80cm) and long enough for it. A width only counts as in stock when a stock
     length at that width is available, so a per-cm Custom Length variant can't sell a width
     whose rolls have run out. Returns { match } or { reason, width } when nothing fits. */
  #antiSlipFor(pack) {
    const lengthCm = this.#runnerLengthCm();
    const current = this.#currentVariant();
    const widthCm = current?.widthCm || this.widthCm;
    if (!lengthCm || !pack?.variants?.length) return { reason: "size" };
    const TOL = 3;
    const lenIdx = Number(pack.lengthIndex);
    const widIdx = Number(pack.widthIndex);
    const all = pack.variants
      .filter((v) => !isCustomLengthVariant(v))
      .map((v) => {
        const opts = v.options || [];
        const lenLabel = Number.isInteger(lenIdx) && lenIdx >= 0 ? opts[lenIdx] : "";
        const widLabel = Number.isInteger(widIdx) && widIdx >= 0 ? opts[widIdx] : "";
        return { ...v, len: RR.toCm(lenLabel), wid: RR.toCm(widLabel), lenLabel, widLabel };
      })
      .filter((v) => v.len);
    const fits = (v) => !widthCm || !v.wid || v.wid <= widthCm + TOL;
    const longEnough = all.filter((v) => v.len >= lengthCm - TOL);
    const stocked = longEnough.filter((v) => v.available && fits(v));
    if (!stocked.length) {
      /* Why not: the width that would fit exists but is sold out, or none is narrow enough. */
      const sized = all.filter(fits).sort((a, b) => b.wid - a.wid)[0];
      return sized ? { reason: "oos", width: sized.wid, widthLabel: sized.widLabel } : { reason: "wide" };
    }
    const bestWid = Math.max(...stocked.map((v) => v.wid || 0));
    const atWid = stocked.filter((v) => Math.abs((v.wid || 0) - bestWid) <= TOL);
    if (current?.exact?.ok) {
      const perCm = exactUnderlayVariant(pack, bestWid || widthCm, current.exact.cm, RR.toCm);
      if (perCm && (!bestWid || Math.abs(perCm.wid - bestWid) <= TOL)) return { match: { ...perCm, lenLabel: current.exact.inputLabel } };
    }
    const minLen = Math.min(...atWid.map((v) => v.len));
    return { match: atWid.find((v) => Math.abs(v.len - minLen) <= TOL) || atWid[0] };
  }

  /* Which quality to use. The customer's pick if it fits; otherwise the first quality in the
     block's list at the best width any quality can offer (Basic Fleece by default). When the
     default can't be used, `swapped` explains why on the card. */
  #antiSlipChoice() {
    const rows = this.qualities.map((q) => ({ q, ...this.#antiSlipFor(q) }));
    const ok = rows.filter((r) => r.match);
    if (!ok.length) {
      return { rows, match: null, none: rows.some((r) => r.reason !== "size") };
    }
    const bestWid = Math.max(...ok.map((r) => r.match.wid || 0));
    const atBest = ok.filter((r) => Math.abs((r.match.wid || 0) - bestWid) <= 3);
    const picked = this.qualityPicked && ok.find((r) => r.q.handle === this.qualityPicked);
    const chosen = picked || atBest[0];
    const first = rows[0];
    let swapped = "";
    if (!picked && chosen.q !== first.q && first.reason === "oos") {
      swapped = (this.antiSlip.copy?.swapped || "")
        .replace(/\{width\}/g, RR.label(first.widthLabel) || RR.len(first.width))
        .replace(/\{name\}/g, first.q.name || first.q.title)
        .replace(/\{pick\}/g, chosen.q.name || chosen.q.title);
    }
    this.data.underlay = chosen.q;
    return { rows, chosen, match: { ...chosen.match, quality: chosen.q }, swapped };
  }

  #extras() {
    let extra = 0;
    const tape = this.tapeOn ? this.#tapeMatch() : null;
    if (tape?.variant) extra += tape.variant.price;
    const underlay = this.underlayOn ? this.#underlayMatch() : null;
    if (underlay) extra += underlay.price;
    return { extra, tape, underlay };
  }

  #label() {
    const state = this.#state();
    if (state === "width") return RR.t("choose_width");
    if (state === "length") return RR.t("choose_length");
    if (state === "oos") return RR.t("stock_out");
    return RR.t("add_to_basket");
  }

  #render() {
    const state = this.#state();
    const variant = this.#currentVariant();
    if (this.widthMenu) this.widthMenu.value = this.widthCm ? String(this.widths.find((w) => near(w.cm, this.widthCm, 1))?.cm || "") : "";
    this.#fillLengths();

    const standard = this.querySelector("[data-stair-length-standard]");
    const custom = this.querySelector("[data-stair-custom]");
    if (standard) standard.hidden = this.customOn;
    if (custom) custom.hidden = !this.customOn;
    const toggle = this.querySelector("[data-stair-custom-label]");
    if (toggle) toggle.textContent = this.customOn ? RR.t("custom_toggle_back") : RR.t("custom_toggle");
    const unit = this.querySelector("[data-stair-custom-unit]");
    const customInput = this.querySelector("[data-stair-custom-input]");
    const customBox = customInput?.closest(".stair-custom__box");
    /* Choosing a standard length (or the guide) clears the custom value: clear the boxes too,
       so reopening custom length never shows an old number that is no longer in use. */
    if (!this.customValue) {
      this.querySelectorAll("[data-stair-custom-input], [data-stair-custom-ft], [data-stair-custom-in]").forEach((input) => {
        if (input.value && document.activeElement !== input) input.value = "";
      });
    }
    if (unit) unit.hidden = !this.customValue;
    if (customBox) customBox.classList.toggle("is-filled", Boolean(this.customValue));
    if (customInput) customInput.style.width = this.customValue ? `${this.customValue.length}ch` : "";
    const links = this.querySelector(".stair-links");
    /* The toggle only appears on runners that have a per-cm Custom Length variant (the rest keep stock lengths only). */
    if (links) links.hidden = !this.#hasExactLength();
    const help = this.querySelector("[data-stair-custom-help]");
    const note = this.querySelector("[data-stair-exact-note]");
    const exactVariant = this.customOn ? this.#exactVariant() : null;
    const exact = exactVariant ? this.#exact() : null;
    const rounded = this.customOn && !exactVariant ? this.#roundedCustom() : null;
    /* Only say a length is too short once it looks finished: two digits, or anything in the feet box. */
    const typedEnough = this.customValue.length >= 2 || Boolean(this.querySelector("[data-stair-custom-ft]")?.value);
    if (help) {
      help.hidden = !this.customOn;
      let text = "";
      let invalid = false;
      if (exactVariant) {
        /* Exact length: priced to the centimetre, so the help line is the price (or why there isn't one yet). */
        const limits = { min: exact.minLabel, max: exact.maxLabel, width: this.widthText };
        if (exact.ok) {
          text = RR.t("custom_exact_price", { length: exact.inputLabel, price: RR.money(exact.priceCents) });
        } else if (exact.reason === "min" && typedEnough) {
          text = RR.t("custom_min", limits);
          invalid = true;
        } else if (exact.reason === "max") {
          text = RR.t("custom_max", limits);
          invalid = true;
        }
        /* Nothing typed yet (or not enough to judge): no help line, just the "Cut to your exact length" note. */
      } else {
        const tooSmall = this.customValue && Number(this.customValue) < CUSTOM.min && typedEnough;
        const limits = { min: CUSTOM.show(CUSTOM.min), max: CUSTOM.maxText, step: CUSTOM.step };
        text = tooSmall
          ? RR.t("custom_too_small", limits)
          : rounded && !rounded.exact
            ? RR.t("custom_rounded", { step: CUSTOM.step, input: rounded.input, length: rounded.text })
            : RR.t("custom_help", limits);
        invalid = Boolean(tooSmall);
      }
      help.textContent = text;
      if (!text) help.hidden = true;
      help.classList.toggle("is-invalid", invalid);
      help.classList.toggle("stair-help--price", Boolean(exact?.ok));
      custom?.classList.toggle("is-invalid", invalid);
    }
    if (note) note.hidden = !(this.customOn && exactVariant);

    this.#renderPrice(state, variant);
    this.#renderTotal(state, variant);
    this.#renderStock(state, variant);
    this.#renderTape();
    this.#renderUnderlay();
    this.#applyLabel();
    this.#renderSticky(state, variant);
    if (this.customOn && this.widthCm && (rounded || exact?.ok)) this.#syncVariant();
  }

  #renderPrice(state, variant) {
    const prefix = document.querySelector("[data-rug-price-prefix]");
    const now = document.querySelector("[data-rug-price-now]");
    const was = document.querySelector("[data-rug-price-was]");
    const save = document.querySelector("[data-rug-price-save]");
    if (!now) return;
    /* "oos" with no variant is a width/length pair that doesn't exist: show the from price
       rather than reading variant.price off null (live Noibu issues #361 and #367). */
    if ((state !== "add" && state !== "oos") || !variant) {
      if (prefix) prefix.hidden = false;
      now.textContent = RR.money(this.data.minPrice);
      now.classList.remove("rug-price__now--sale");
      if (was) was.hidden = true;
      if (save) save.hidden = true;
      return;
    }
    if (prefix) prefix.hidden = true;
    now.textContent = RR.money(variant.price);
    const onSale = variant.compare > variant.price;
    now.classList.toggle("rug-price__now--sale", onSale);
    if (was) {
      was.hidden = !onSale;
      was.textContent = onSale ? RR.money(variant.compare) : "";
    }
    if (save) {
      save.hidden = !onSale;
      save.textContent = onSale ? RR.t("save_amount", { amount: RR.money(variant.compare - variant.price) }) : "";
    }
  }

  #renderTotal(state, variant) {
    const box = this.querySelector("[data-stair-total]");
    const value = this.querySelector("[data-stair-total-value]");
    const note = this.querySelector("[data-stair-total-note]");
    if (!box) return;
    if ((state !== "add" && state !== "oos") || !variant) {
      box.hidden = true;
      return;
    }
    const { extra, tape, underlay } = this.#extras();
    if (value) value.textContent = RR.money((variant.price + extra) * this.#quantity());
    const parts = [];
    if (this.tapeOn && tape?.variant) parts.push(RR.t("part_tape"));
    if (this.underlayOn && underlay) parts.push(RR.t("part_underlay"));
    if (note) {
      note.textContent = parts.length ? RR.t("total_runner_plus", { parts: parts.join(" + ") }) : "";
      note.hidden = !parts.length;
    }
    box.hidden = false;
  }

  #renderStock(state, variant) {
    const text = this.querySelector("[data-stair-stock-text]");
    const detail = this.querySelector("[data-stair-stock-detail]");
    const stock = this.querySelector("[data-stair-stock]");
    const oos = state === "oos";
    stock?.classList.toggle("is-oos", oos);
    if (detail) detail.hidden = !oos;
    if (!text) return;
    if (oos) {
      const length = this.length
        ? this.#lengthText(this.length)
        : this.#exact()?.inputLabel || this.#roundedCustom()?.text || RR.t("this_length");
      text.textContent = RR.t("stock_oos_line", { length, width: this.widthText });
      return;
    }
    text.textContent = RR.t("stock_in");
  }

  #setupTape() {
    const tapes = this.data.tapes || [];
    const card = this.querySelector("[data-stair-tape-card]");
    if (!tapes.length) {
      if (card) card.hidden = true;
      return;
    }
    card.hidden = false;
    const thumb = this.querySelector("[data-stair-tape-thumb]");
    if (thumb) {
      thumb.src = tapes[0].photo || tapes[0].swatch || "";
      thumb.alt = "";
    }
    const row = this.querySelector("[data-stair-tape-options]");
    row.innerHTML = tapes
      .map(
        (tape) =>
          `<button type="button" class="stair-swatch" data-stair-tape="${escapeAttr(tape.name)}" aria-pressed="false"><img src="${escapeAttr(tape.swatch || tape.photo || "")}" alt="${escapeAttr(tape.name)}" width="60" height="60"><span>${escapeHtml(tape.name)}</span></button>`,
      )
      .join("");
  }

  #renderTape() {
    const tapes = this.data.tapes || [];
    if (!tapes.length) return;
    const check = this.querySelector("[data-stair-tape-check]");
    if (check) check.checked = this.tapeOn;
    const card = this.querySelector("[data-stair-tape-card]");
    card?.classList.toggle("is-on", this.tapeOn);
    const colours = this.querySelector("[data-stair-tapes]");
    if (colours) colours.hidden = !this.tapeOn;
    const name = this.querySelector("[data-stair-tape-name]");
    if (name) name.textContent = this.tapeName;
    this.querySelectorAll("[data-stair-tape]").forEach((btn) => {
      btn.setAttribute("aria-pressed", String(btn.dataset.stairTape === this.tapeName));
    });
    const match = this.#tapeMatch();
    const price = this.querySelector("[data-stair-tape-price]");
    const fit = this.querySelector("[data-stair-tape-fit]");
    const thumb = this.querySelector("[data-stair-tape-thumb]");
    if (thumb && match?.tape) thumb.src = match.tape.photo || match.tape.swatch || thumb.src;
    const preview = this.querySelector("[data-stair-tape-preview]");
    if (preview && match?.tape?.photo) {
      preview.src = match.tape.photo;
      const title = this.dataset.productTitle || "";
      preview.alt = RR.t("tape_with", { title, tape: match.tape.name.toLowerCase() });
    }
    if (!this.tapeOn) {
      if (price) price.textContent = "";
      if (fit) fit.textContent = RR.t("tape_whipped");
      return;
    }
    if (fit) fit.textContent = match?.variant ? RR.t("tape_sewn") : RR.t("tape_choose_size");
    if (price) {
      if (match?.variant) price.textContent = `+${RR.money(match.variant.price)}`;
      else if (match?.rate) price.textContent = RR.t("tape_rate", { price: RR.money(match.rate) });
      else price.textContent = "";
    }
    this.#showTapePhoto();
  }

  #showTapePhoto() {
    if (!this.tapeOn || !this.tapeName) return;
    const slides = [...document.querySelectorAll(".product-media__wrapper .swiper-slide:not(.swiper-slide-duplicate)")];
    const needle = this.tapeName.toLowerCase();
    const index = slides.findIndex((slide) => {
      const alt = (slide.querySelector("img")?.alt || "").toLowerCase();
      return alt.includes(needle) && /tape|#tape-/.test(alt);
    });
    if (index >= 0) this.#showGalleryPhoto(index);
  }

  #showGalleryPhoto(index) {
    const swiper = document.querySelector(".product-media__wrapper")?.swiper;
    if (!swiper) return;
    if (typeof swiper.slideToLoop === "function") swiper.slideToLoop(index);
    else if (typeof swiper.slideTo === "function") swiper.slideTo(index);
  }

  #setupUnderlay() {
    const card = this.querySelector("[data-stair-underlay-card]");
    const pack = this.data.underlay;
    if (!pack?.variants?.length && !pack?.feed) {
      if (card) card.hidden = true;
      return;
    }
    if (!card) return;
    card.hidden = false;
    const thumb = this.querySelector("[data-stair-underlay-thumb]");
    if (thumb) {
      thumb.src = pack.image || "";
      thumb.alt = "";
    }
  }

  #thicknessText(value) {
    // "0.3inch - Standard" -> "0.3in Standard"; "8mm" stays "8mm".
    return RR.clean(value).replace(/\s*-\s*/g, " ").trim();
  }

  #renderUnderlay() {
    if (!this.data.underlay?.variants?.length) return;
    const check = this.querySelector("[data-stair-underlay-check]");
    if (check) check.checked = this.underlayOn;
    this.querySelector("[data-stair-underlay-card]")?.classList.toggle("is-on", this.underlayOn);
    const steps = this.querySelector("[data-stair-steps]");
    if (steps) steps.hidden = !this.underlayOn;
    const mmRow = this.querySelector("[data-stair-underlay-mm]");
    if (mmRow && !mmRow.dataset.ready) {
      const { thicknesses } = this.#underlayOptions();
      if (!this.underlayThickness) this.underlayThickness = thicknesses[0] || "";
      mmRow.innerHTML = thicknesses
        .map((value) => `<button type="button" data-stair-underlay-mm-opt="${escapeAttr(value)}">${escapeHtml(this.#thicknessText(value))}</button>`)
        .join("");
      mmRow.dataset.ready = "1";
      const label = mmRow.previousElementSibling;
      if (thicknesses.length < 2) {
        mmRow.hidden = true;
        if (label?.classList.contains("rug-label")) label.hidden = true;
      }
    }
    mmRow?.querySelectorAll("[data-stair-underlay-mm-opt]").forEach((btn) => {
      btn.setAttribute("aria-pressed", String(btn.dataset.stairUnderlayMmOpt === this.underlayThickness));
    });
    const input = this.querySelector("[data-stair-steps-input]");
    if (input && document.activeElement !== input) input.value = this.steps ? String(this.steps) : "";
    const fit = this.querySelector("[data-stair-underlay-fit]");
    const price = this.querySelector("[data-stair-underlay-price]");
    const match = this.underlayOn ? this.#underlayMatch() : null;
    const hall = this.#isFlat();
    if (!this.underlayOn) {
      const box = this.querySelector("[data-stair-antislip]");
      if (box) box.hidden = true;
      if (fit) {
        fit.textContent = this.#isKitchen()
          ? RR.t("underlay_kitchen_fit")
          : hall
            ? RR.t("underlay_hall_fit")
            : RR.t("underlay_stair_fit");
      }
      if (price) price.textContent = "";
      return;
    }
    if (hall && this.qualities.length) {
      this.#renderAntiSlip(fit, price);
      return;
    }
    if (hall) {
      if (!this.#runnerLengthCm() || !match) {
        if (fit) fit.textContent = RR.t("underlay_choose_size");
        if (price) price.textContent = "";
        return;
      }
      const size = [match.lenLabel ? RR.label(match.lenLabel) : "", match.widLabel ? RR.label(match.widLabel) : ""]
        .filter(Boolean)
        .join(" × ");
      if (fit) fit.textContent = size ? RR.t("underlay_cut_to", { size }) : match.title;
      if (price) price.textContent = `+${RR.money(match.price)}`;
      return;
    }
    if (!this.steps || !match) {
      if (fit) fit.textContent = RR.t("underlay_enter_steps");
      if (price) price.textContent = "";
      return;
    }
    if (fit) fit.textContent = [this.#thicknessText(match.thickness), match.padText || RR.t("underlay_pads", { count: match.pads })].filter(Boolean).join(" · ");
    if (price) price.textContent = `+${RR.money(match.price)}`;
  }

  /* The anti-slip card when ticked: the chosen quality, its cut size and price, a note when
     the default quality is out of stock in the runner's width, and a compact list of every
     quality with its own width and price for this runner (sold out widths shown, not offered). */
  #renderAntiSlip(fit, price) {
    const copy = this.antiSlip.copy || {};
    const box = this.querySelector("[data-stair-antislip]");
    const list = this.querySelector("[data-stair-antislip-options]");
    const note = this.querySelector("[data-stair-antislip-note]");
    const label = this.querySelector("[data-stair-antislip-label]");
    const thumb = this.querySelector("[data-stair-underlay-thumb]");
    const card = this.querySelector("[data-stair-underlay-card]");
    if (!this.#runnerLengthCm()) {
      if (fit) fit.textContent = RR.t("underlay_choose_size");
      if (price) price.textContent = "";
      if (box) box.hidden = true;
      card?.classList.remove("is-none");
      return;
    }
    const choice = this.#antiSlipChoice();
    const sizeOf = (m) => [m.lenLabel ? RR.label(m.lenLabel) : "", m.widLabel ? RR.label(m.widLabel) : ""].filter(Boolean).join(" × ");
    card?.classList.toggle("is-none", !choice.match);
    if (!choice.match) {
      if (fit) fit.textContent = copy.none || RR.t("underlay_choose_size");
      if (price) price.textContent = "";
      if (note) note.hidden = true;
    } else {
      const q = choice.chosen.q;
      const size = sizeOf(choice.match);
      if (fit) fit.textContent = (copy.cutTo || "{name}: {size}").replace("{name}", q.name || q.title).replace("{size}", size);
      if (price) price.textContent = `+${RR.money(choice.match.price)}`;
      if (thumb && q.image) thumb.src = q.image;
      if (note) {
        note.textContent = choice.swapped || "";
        note.hidden = !choice.swapped;
      }
    }
    if (!box || !list) return;
    box.hidden = this.qualities.length < 2;
    if (label) label.textContent = copy.choose || "";
    const chosenHandle = choice.chosen?.q?.handle || "";
    list.innerHTML = choice.rows
      .map((r) => {
        const on = r.q.handle === chosenHandle;
        const off = !r.match;
        const right = r.match
          ? `<span class="stair-antislip__wid">${escapeHtml(r.match.widLabel ? RR.label(r.match.widLabel) : "")}</span><strong class="stair-antislip__price">${escapeHtml(RR.money(r.match.price))}</strong>`
          : `<span class="stair-antislip__oos">${escapeHtml(
              r.reason === "oos" ? (copy.oos || "{width}").replace("{width}", RR.label(r.widthLabel) || RR.len(r.width)) : copy.noWidth || "",
            )}</span>`;
        return `<button type="button" class="stair-antislip__opt" role="radio" aria-checked="${on}" aria-disabled="${off}" data-stair-antislip-opt="${escapeAttr(r.q.handle)}"${off ? ' tabindex="-1"' : ""}>
          <span class="stair-antislip__dot" aria-hidden="true"></span>
          <span class="stair-antislip__copy"><strong>${escapeHtml(r.q.name || r.q.title)}</strong>${r.q.bestFor ? `<span>${escapeHtml(r.q.bestFor)}</span>` : ""}</span>
          <span class="stair-antislip__right">${right}</span>
        </button>`;
      })
      .join("");
  }

  /* Pick the first stock length at least as long as the need (cm). */
  #choiceFor(needCm) {
    return this.lengthChoices.find((c) => c.cm >= needCm - 0.5) || this.lengthChoices[this.lengthChoices.length - 1] || null;
  }

  #renderGuide() {
    const unit = RR.imperial ? "in" : "cm";
    const toCm = (n) => (RR.imperial ? n * 2.54 : n);
    const show = (n) => `${n}${RR.imperial ? "" : RR.cfg.unitSpace || ""}${unit}`;
    if (this.#isFlat()) {
      const hall = Number(this.querySelector('[data-stair-g="hall"]')?.value || 0);
      const wait = this.querySelector("[data-stair-guide-wait]");
      const result = this.querySelector("[data-stair-guide-result]");
      const apply = this.querySelector("[data-stair-guide-apply]");
      const ready = toCm(hall) >= 30;
      if (wait) wait.hidden = ready;
      if (result) result.hidden = !ready;
      if (apply) apply.hidden = !ready;
      if (!ready || !result) return;
      const choice = this.#choiceFor(toCm(hall));
      if (!choice) return;
      this.guideRec = choice.label;
      result.innerHTML = `<table><tbody>
        <tr><td>${escapeHtml(RR.t("guide_your_hall"))}</td><td>${show(hall)}</td></tr>
        <tr><td>${escapeHtml(RR.t("guide_rounded"))}</td><td>${escapeHtml(choice.text)}</td></tr>
      </tbody></table>
      <p class="stair-guide__rec"><span>${escapeHtml(RR.t("guide_recommend"))}</span><strong>${escapeHtml(choice.text)}</strong></p>`;
      if (apply) apply.textContent = RR.t("guide_select_length", { length: choice.text });
      return;
    }
    const steps = Number(this.querySelector('[data-stair-g="steps"]')?.value || 0);
    const tread = Number(this.querySelector('[data-stair-g="tread"]')?.value || 0);
    const riser = Number(this.querySelector('[data-stair-g="riser"]')?.value || 0);
    const wait = this.querySelector("[data-stair-guide-wait]");
    const result = this.querySelector("[data-stair-guide-result]");
    const apply = this.querySelector("[data-stair-guide-apply]");
    const ready = steps > 0 && tread > 0 && riser > 0;
    if (wait) wait.hidden = ready;
    if (result) result.hidden = !ready;
    if (apply) apply.hidden = !ready;
    if (!ready || !result) return;
    const per = tread + riser;
    const raw = steps * per;
    const withWaste = Math.ceil(raw * 1.1);
    const choice = this.#choiceFor(toCm(withWaste));
    if (!choice) return;
    this.guideRec = choice.label;
    result.innerHTML = `<table><tbody>
      <tr><td>${escapeHtml(RR.t("guide_per_step", { tread: show(tread), riser: show(riser) }))}</td><td>${show(per)}</td></tr>
      <tr><td>${escapeHtml(RR.t("guide_times_steps", { steps }))}</td><td>${show(raw)}</td></tr>
      <tr><td>${escapeHtml(RR.t("guide_wastage"))}</td><td>${show(withWaste - raw)}</td></tr>
      <tr><td>${escapeHtml(RR.t("guide_rounded"))}</td><td>${escapeHtml(choice.text)}</td></tr>
    </tbody></table>
    <p class="stair-guide__rec"><span>${escapeHtml(RR.t("guide_recommend"))}</span><strong>${escapeHtml(choice.text)}</strong></p>`;
    if (apply) apply.textContent = RR.t("guide_select_length", { length: choice.text });
  }

  #applyGuide() {
    if (!this.guideRec) return;
    const choice = this.lengthChoices.find((c) => c.label === this.guideRec);
    if (!choice) return;
    try {
      const g = (key) => Number(this.querySelector(`[data-stair-g="${key}"]`)?.value || 0);
      const toCm = (n) => (RR.imperial ? Math.round(n * 2.54) : n);
      track("pdp_guide_apply", {
        steps: g("steps") || undefined,
        tread_cm: toCm(g("tread")) || undefined,
        riser_cm: toCm(g("riser")) || undefined,
        length_cm: choice.cm || undefined,
        product_handle: productHandle(),
      });
    } catch {
      /* no-op */
    }
    this.customOn = false;
    this.customValue = "";
    this.length = choice.label;
    this.querySelector("[data-stair-guide]").hidden = true;
    if (!this.widthCm) {
      this.#render();
      this.nudge();
      return;
    }
    this.#syncVariant();
    this.#render();
  }

  #watchCartButton() {
    this.submit = this.form?.querySelector('add-to-cart-component button[type="submit"]');
    this.submitLabel = this.submit?.querySelector(".button-label") || this.submit;
    if (!this.submitLabel) return;
    this.observer = new MutationObserver(() => this.#applyLabel());
    this.observer.observe(this.submitLabel, { childList: true, characterData: true, subtree: true });
  }

  #applyLabel() {
    if (!this.submitLabel || this.applying) return;
    const label = this.#label();
    const state = this.#state();
    if (this.submit) this.submit.dataset.stairState = state === "oos" ? "oos" : state === "add" ? "add" : "choose";
    if (this.submitLabel.textContent.trim() === label) return;
    this.submitLabel.textContent = label;
  }

  #relabelSample() {
    const text = this.querySelector("[data-sample-text]");
    const btn = this.querySelector("[data-sample-button]");
    if (!text || !btn || btn.hasAttribute("data-sample-active")) return;
    const price = this.data.samplePrice ? ` · ${RR.money(this.data.samplePrice)}` : "";
    const label = `${RR.t("order_sample")}${price}`;
    text.dataset.sampleAddText = label;
    text.textContent = label;
  }

  #onSubmit(e) {
    if (e.target !== this.form) return;
    /* pdp_atc_click: one per press. The sticky bar sets its source before it clicks the
       real button; the replay after the add-on feeds load is not a second press. */
    if (this._atcReplay) {
      this._atcReplay = false;
    } else {
      const source = this._atcSource || "main";
      this._atcSource = "";
      track("pdp_atc_click", this.#atcPropsSafe(source));
    }
    if (this.feedsLoading && (this.tapeOn || this.underlayOn)) {
      /* Tape or underlay ticked before their prices arrived: wait, then add with them. */
      e.preventDefault();
      e.stopImmediatePropagation();
      this.feedsReady?.then(() => {
        this._atcReplay = true;
        this.submit?.click();
        /* A disabled button never submits; don't let the flag swallow the next real press. */
        this._atcReplay = false;
      });
      return;
    }
    const state = this.#state();
    if (state !== "add") {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.nudge();
      return;
    }
    const variant = this.#currentVariant();
    const { tape, underlay } = this.#extras();
    const edge = this.querySelector("[data-stair-edge]");
    const requested = this.querySelector("[data-stair-requested]");
    // Line item properties stay in English. Sizes are in the shopper's units (feet and inches in the USA
    // and Canada); the exact-length line also keeps _Length cm for the warehouse.
    if (edge) {
      if (this.tapeOn && tape?.variant) {
        edge.disabled = false;
        edge.value = `${tape.tape.name} tape`;
      } else {
        edge.disabled = true;
        edge.value = "";
      }
    }
    const exact = variant?.exact || null;
    const rounded = this.customOn && !exact ? this.#roundedCustom() : null;
    if (requested) {
      if (rounded && !rounded.exact) {
        requested.disabled = false;
        // Shown at checkout as typed, so USA and Canada see feet and inches only.
        requested.value = `${rounded.input} (cut as ${rounded.text})`;
      } else {
        requested.disabled = true;
        requested.value = "";
      }
    }
    /* Exact length: the same line properties as the exact-length box on the older
       runner templates, so the cart transform, the cart rows and the cutters see one shape. */
    const bundleId = exact ? createBundleId() : "";
    const exactProps = exact
      ? exactLineProperties({
          cm: exact.cm,
          priceCents: exact.priceCents,
          widthLabel: this.widthText,
          display: exact.display,
          bundleId,
        })
      : null;
    this.querySelectorAll("[data-stair-exact]").forEach((input) => {
      const value = exactProps?.[input.dataset.stairExact];
      input.disabled = !value;
      input.value = value || "";
    });
    const items = [];
    const qty = this.#quantity();
    const sizeText = `${this.widthText} × ${variant?.lengthText || ""}`.trim();
    if (this.tapeOn && tape?.variant) {
      const forLabel = `${this.dataset.productTitle} ${sizeText}`;
      const properties = tape.variant.perCm
        ? exactTapeProperties({
            forLabel,
            bundleId,
            cm: tape.variant.lengthCm,
            priceCents: tape.variant.price,
            widthLabel: this.widthText,
            display: exact?.display || "",
          })
        : exact
          ? { _For: forLabel, _calculator_bundle_id: bundleId, _calculator_addon: "tape_binding" }
          : { _For: forLabel };
      items.push({ id: tape.variant.id, quantity: qty, properties });
    }
    if (this.underlayOn && underlay) {
      const forLabel = this.#isFlat()
        ? `${this.dataset.productTitle} · ${sizeText}`.trim()
        : `${this.dataset.productTitle} · ${this.steps} steps`;
      const properties = underlay.perCm
        ? exactUnderlayProperties({
            forLabel,
            bundleId,
            cm: underlay.lengthCm,
            priceCents: underlay.price,
            widthLabel: RR.label(underlay.widLabel),
            display: exact?.display || "",
          })
        : { _For: forLabel };
      items.push({ id: underlay.id, quantity: qty, properties });
    }
    this.pending = items.length ? items : null;
  }

  nudge() {
    const state = this.#state();
    const field = this.querySelector(`[data-stair-field="${state === "width" ? "width" : "length"}"]`);
    field?.classList.add("is-invalid");
    field?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  async #addPending() {
    const items = this.pending;
    if (!items?.length) return;
    this.pending = null;
    try {
      await fetch(shopifyCartUrl("cart/add.js"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });
      const res = await fetch(shopifyCartUrl("cart.js"));
      if (res.ok) window.__cart = await res.json();
      updateCartBubble();
      ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
    } catch (err) {
      console.error("[StairPDP] Add-on failed", err);
    }
  }

  #mountSticky() {
    const bar = this.querySelector("[data-stair-sticky]");
    if (!bar) return;
    document.body.appendChild(bar);
    bar.querySelector("[data-stair-sticky-cta]")?.addEventListener("click", () => {
      if (this.#state() === "add") {
        /* #onSubmit sends pdp_atc_click with source "sticky". */
        this._atcSource = "sticky";
        this.submit?.click();
        /* submit fires synchronously inside click(); clear the flag if it never did. */
        setTimeout(() => {
          this._atcSource = "";
        }, 0);
      } else {
        track("pdp_atc_click", this.#atcPropsSafe("sticky"));
        this.nudge();
      }
    });
    const target = this.submit;
    if (!target || !("IntersectionObserver" in window)) return;
    new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle("is-visible", passed);
      bar.setAttribute("aria-hidden", String(!passed));
      const cta = bar.querySelector("[data-stair-sticky-cta]");
      if (cta) cta.tabIndex = passed ? 0 : -1;
    }).observe(target);
  }

  #renderSticky(state, variant) {
    const bar = document.querySelector("[data-stair-sticky]");
    if (!bar) return;
    const size = bar.querySelector("[data-stair-sticky-size]");
    const price = bar.querySelector("[data-stair-sticky-price]");
    const cta = bar.querySelector("[data-stair-sticky-cta]");
    const chosen = state === "add" || state === "oos";
    if (size) size.textContent = chosen ? ` · ${this.widthText} × ${variant?.lengthText || this.#lengthText(this.length)}` : "";
    if (price) {
      const extra = chosen && variant ? this.#extras().extra : 0;
      price.textContent = chosen && variant ? RR.money(variant.price + extra) : RR.t("from_price", { price: RR.money(this.data.minPrice) });
    }
    if (cta) {
      cta.textContent =
        state === "width"
          ? RR.t("sticky_choose_width")
          : state === "length"
            ? RR.t("sticky_choose_length")
            : state === "oos"
              ? RR.t("stock_out")
              : RR.t("add_to_basket");
      cta.dataset.rugState = state === "oos" ? "notify" : state === "add" ? "add" : "choose";
    }
    const img = bar.querySelector("[data-stair-sticky-img]");
    if (img) {
      const tape = this.tapeOn ? this.#tapeMatch()?.tape : null;
      const next = this.tapeOn ? tape?.photo || tape?.swatch || "" : img.dataset.defaultSrc || "";
      if (next && img.getAttribute("src") !== next) {
        img.removeAttribute("srcset");
        img.removeAttribute("sizes");
        img.src = next;
      }
    }
  }
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);
}
function escapeAttr(value) {
  return escapeHtml(value);
}

if (!customElements.get("stair-runner")) customElements.define("stair-runner", StairRunner);

function kitchenRunnerIsLight(hex) {
  const raw = String(hex || "").replace("#", "").trim();
  const n = raw.length === 3 ? [...raw].map((ch) => ch + ch).join("") : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(n)) return false;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 170;
}

function initKitchenPlacement() {
  document.querySelectorAll("[data-kitchen-place]").forEach((root) => {
    root.classList.toggle("kitchen-place--light", kitchenRunnerIsLight(root.dataset.kitchenRunner));
    root.querySelectorAll(".kitchen-place__svg img").forEach((img) => {
      fetch(img.src, { credentials: "same-origin" })
        .then((res) => (res.ok ? res.text() : ""))
        .then((svg) => {
          if (!svg.includes("<svg")) return;
          const wrap = document.createElement("div");
          wrap.innerHTML = svg.trim();
          const node = wrap.querySelector("svg");
          if (!node) return;
          img.replaceWith(node);
        })
        .catch(() => {});
    });
    const track = root.querySelector("[data-kitchen-place-track]");
    const bar = root.querySelector("[data-kitchen-place-bar]");
    const thumb = root.querySelector("[data-kitchen-place-thumb]");
    if (!track || !bar || !thumb) return;
    const maxScroll = () => track.scrollWidth - track.clientWidth;
    const trackRoom = () => Math.max(0, bar.clientWidth - thumb.offsetWidth);
    const sync = () => {
      const overflow = track.scrollWidth > track.clientWidth + 1;
      bar.hidden = !overflow;
      track.tabIndex = overflow ? 0 : -1;
      if (!overflow) return;
      const ratio = track.clientWidth / track.scrollWidth;
      thumb.style.width = `${Math.max(36, bar.clientWidth * ratio)}px`;
      const room = trackRoom();
      const left = maxScroll() > 0 ? (track.scrollLeft / maxScroll()) * room : 0;
      thumb.style.transform = `translateX(${left}px)`;
    };
    const scrollFromClientX = (clientX) => {
      const room = trackRoom();
      if (room <= 0) return;
      const x = clientX - bar.getBoundingClientRect().left - thumb.offsetWidth / 2;
      track.scrollLeft = (Math.min(room, Math.max(0, x)) / room) * maxScroll();
    };
    track.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    if (typeof ResizeObserver !== "undefined") new ResizeObserver(sync).observe(track);
    let dragging = false;
    thumb.addEventListener("pointerdown", (event) => {
      dragging = true;
      thumb.setPointerCapture(event.pointerId);
      scrollFromClientX(event.clientX);
      event.preventDefault();
    });
    thumb.addEventListener("pointermove", (event) => {
      if (dragging) scrollFromClientX(event.clientX);
    });
    const stopDrag = () => {
      dragging = false;
    };
    thumb.addEventListener("pointerup", stopDrag);
    thumb.addEventListener("pointercancel", stopDrag);
    bar.addEventListener("pointerdown", (event) => {
      if (event.target === thumb) return;
      scrollFromClientX(event.clientX);
    });
    sync();
  });
}

initKitchenPlacement();
